import { useEffect, useState, Fragment } from "react";
import HttpClient, { Methods } from "../services/HttpClient";
import { endpoints } from "../constants/Endpoints";
import ProfileService, { UserRoles } from "../services/ProfileService";
import { appRoutes } from "../constants/AppRoutes";
import TranslationService from "../services/TranslationService";
import SHBLeadBadge from "./SHBLeadBadge";
import { useMediaQuery } from "react-responsive";
import { MediaQueries } from "./MediaQueries";
import { Link } from "react-router-dom";

// MUI v5 imports
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeIcon from "@mui/icons-material/Home";
import StorageIcon from "@mui/icons-material/Storage";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";

const drawerWidth = 240;

// Styled components replicating old design logic
const Root = styled("div")(({ theme }) => ({
  display: "flex", // row direction so persistent Drawer reserves horizontal space
  minHeight: "100vh",
  position: "relative",
  width: "100%",
}));

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== "open" && prop !== "shifted" && prop !== "inactive",
})(({ theme, open, shifted, inactive }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  zIndex: 1000,
  backgroundColor: inactive ? "#ec2027" : undefined,
  ...(shifted && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const Content = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "mobile",
})(({ theme, open, mobile }) => ({
  flexGrow: 1,
  // Reserve space below the AppBar (toolbar mixin) then small extra spacing
  paddingTop: `calc(${theme.mixins.toolbar.minHeight || 64}px + ${theme.spacing(
    1
  )})`,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: !mobile ? -drawerWidth : 0,
  display: "block",
  overflow: "auto",
  ...(open &&
    !mobile && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
}));

const InactiveText = styled("div", {
  shouldForwardProp: (prop) => prop !== "shift",
})(({ theme, shift }) => ({
  color: "white",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
  }),
  marginLeft: 0,
  ...(shift && { marginLeft: drawerWidth - 40 }),
}));

const Overlay = styled("div", {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  pointerEvents: active ? "all" : "none",
  display: active ? "block" : "none",
  position: "fixed",
  zIndex: 1025,
  inset: 0,
  backgroundColor: "black",
  opacity: 0.5,
}));

export default function CustomDrawer({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(MediaQueries.TabletOrMobileDevice);
  const [open, setOpen] = useState(!isMobile);
  const [isActive, setIsActive] = useState(true);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  useEffect(() => {
    setOpen(!isMobile); // adjust when breakpoint changes
  }, [isMobile]);

  useEffect(() => {
    const fetch = async () => {
      if (ProfileService.isInRole(UserRoles.renter)) {
        try {
          const resp = await HttpClient.doRequest(
            Methods.GET,
            endpoints.renter.getActiveStatus
          );
          if (resp.ok) {
            const json = await resp.json();
            setIsActive(json.isActive);
          }
        } catch {
          /* ignore */
        }
      }
    };
    fetch();
  }, []);

  const navItem = (to, icon, label, badge) => (
    <ListItem
      component={Link}
      to={to}
      onClick={isMobile ? handleDrawerClose : undefined}
      sx={{ color: "unset", "&:hover": { color: "var(--mdc-theme-primary)" } }}
      disablePadding
    >
      <ListItemButton sx={{ py: 1.2, px: 2 }}>
        <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
        <ListItemText primary={badge ? <span>{badge}</span> : label} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Root id="codellic-drawer">
      <CssBaseline />
      <StyledAppBar
        position="fixed"
        sx={{ marginTop: 0, zIndex: (t) => t.zIndex.drawer + 1 }}
        shifted={!isMobile && open}
        inactive={!isActive}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, p: "12px", ...(open && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>
          {!isActive && (
            <InactiveText shift={!isMobile && open}>
              <Typography
                variant="body1"
                sx={{
                  width: "100%",
                  textAlign: "left",
                  color: "white",
                  fontSize: isMobile ? ".8em" : "1em",
                }}
              >
                {TranslationService.l.inschrijvingVerlopen}
              </Typography>
            </InactiveText>
          )}
          <Box sx={{ ml: "auto" }}>
            <Button
              href={
                ProfileService.getLocale() === "en_US"
                  ? "https://www.stadhuis.nl/en/woning-huren"
                  : "https://www.stadhuis.nl/woning-huren"
              }
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              sx={{
                ml: 0,
                "&:hover": { color: "#ffffff" },
              }}
            >
              {isMobile ? (
                <HomeWorkIcon />
              ) : (
                TranslationService.l.naarWoningaanbod
              )}
            </Button>
          </Box>
        </Toolbar>
      </StyledAppBar>
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            marginTop: "0",
            width: drawerWidth,
            boxSizing: "border-box",
            zIndex: 1040,
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <Box id="wrapper-header-logo" sx={{ textAlign: "center", py: 1 }}>
          <Box
            component="img"
            id="header-logo"
            src="/media/img/shb-logo.png"
            alt="logo"
            style={{ width: "100%" }}
            sx={{ maxWidth: "100%", height: "auto", p: 2 }}
          />
        </Box>
        <Divider />
        <List sx={{ p: 0 }}>
          {ProfileService.isInRole(UserRoles.shb) && (
            <Fragment>
              {navItem(
                appRoutes.shb.renter.overview,
                <PeopleIcon />,
                TranslationService.l.inschrijvingen
              )}
              {navItem(
                appRoutes.shb.archive.overview,
                <StorageIcon />,
                TranslationService.l.archief
              )}
              <Divider />
              {navItem(
                appRoutes.shb.lead.overview,
                <BusinessIcon />,
                null,
                <SHBLeadBadge>Huurprijsaanvragen</SHBLeadBadge>
              )}
              {navItem(
                appRoutes.shb.lead.rentalPriceCalculationArchiveOverview,
                <StorageIcon />,
                TranslationService.l.archief
              )}
              <Divider />
              {navItem(
                appRoutes.shb.property.overview,
                <HomeIcon />,
                TranslationService.l.woningen
              )}
              <Divider />
            </Fragment>
          )}
          {ProfileService.isInRole(UserRoles.codellic) && (
            <Fragment>
              {navItem(
                appRoutes.codellic.home,
                <HomeIcon />,
                TranslationService.l.codellicHome
              )}
              <Divider />
            </Fragment>
          )}
          {ProfileService.isInRole(UserRoles.renter) && (
            <Fragment>
              {navItem(
                appRoutes.renter.profile,
                <AccountCircleIcon />,
                TranslationService.l.huurderProfiel
              )}
              <Divider />
              {navItem(
                appRoutes.renter.documents,
                <DescriptionIcon />,
                TranslationService.l.mijnDocumenten
              )}
              <Divider />
              {navItem(
                appRoutes.renter.settings,
                <SettingsIcon />,
                TranslationService.l.instellingen
              )}
              <Divider />
            </Fragment>
          )}
          {ProfileService.isInRole(UserRoles.landlord) && (
            <Fragment>
              {navItem(
                appRoutes.landlord.home,
                <HomeIcon />,
                TranslationService.l.verhuurderProfiel
              )}
              <Divider />
            </Fragment>
          )}
          <ListItem
            component={Link}
            to=""
            onClick={() => (window.location.href = "/Identity/Account/Logout")}
            disablePadding
            sx={{
              color: "unset",
              "&:hover": { color: "var(--mdc-theme-primary)" },
            }}
          >
            <ListItemButton sx={{ py: 1.2, px: 2 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary={TranslationService.l.logUit} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      <Content open={open} mobile={isMobile}>
        {children}
      </Content>
    </Root>
  );
}
